import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import Image from 'next/image'
import { useCallback, useContext } from 'react'
import { HLSContext } from '../../context/hlsContext'
import { ViddyCoreContext } from '../../context/viddyCoreContext'
import { VideoContext } from '../../context/viddyVideoContext'
import { PlayIcon } from '../icons/Icons'

type RGBColor = {
    r: number
    g: number
    b: number
}

const pulseWhite = () => keyframes`
    0% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.7);
    }

    70% {
        transform: scale(1.5);
        box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
    }

    100% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
    }
`

const pulseColorKeyframes = (pulseColor: RGBColor) => keyframes`
    0% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(${pulseColor.r}, ${pulseColor.g}, ${pulseColor.b}, 0.7);
    }
    70% {
        transform: scale(1.5);
        box-shadow: 0 0 0 10px rgba(${pulseColor.r}, ${pulseColor.g}, ${pulseColor.b}, 0);
    }

    100% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(${pulseColor.r}, ${pulseColor.g}, ${pulseColor.b}, 0);
    }
`

const PulseBlob = styled.div(
    {
        background: 'rgb(0,0,0,0.5)',
        borderRadius: '50%',
        boxShadow: '0 0 0 0 rgba(0, 0, 0, 1)',
        height: 80,
        width: 80,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        margin: 'auto',
        transform: 'scale(1)',
        animation: `${pulseWhite()} 3s infinite`,
        position: 'absolute',
    },
    ({ pulseColor }: { pulseColor: RGBColor }) => ({
        boxShadow: `0 0 0 0 rgba(${pulseColor.r}, ${pulseColor.g}, ${pulseColor.b}, 1)`,
        backgroundColor: `rgba(${pulseColor.r}, ${pulseColor.g}, ${pulseColor.b},0.9)`,
        animation: `${pulseColorKeyframes(pulseColor)} 3s infinite`,
    }),
)

const playIconPulse = () => keyframes`
    0% {
        transform: scale(0.96);
    }

    70% {
        transform: scale(1.08);
    }

    100% {
        transform: scale(0.96);
    }
`

const PlayIconWrapper = styled.div({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: `${playIconPulse()} 3s infinite`,
})

const PlayButtonContainer = styled.div({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    zIndex: 3,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    pointerEvents: 'auto',
})

const CenterWrapper = styled.div({
    position: 'relative',
    width: 80,
    height: 80,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
})

export const PlayButtonOverlay: React.FC = () => {
    const {
        indexes: { subIndexes, mainIndex },
    } = useContext(ViddyCoreContext)
    const { isLPM, setIsLPM, setIsMuted, setVideoTapped, videoTapped } =
        useContext(VideoContext)
    const { hlsInstances } = useContext(HLSContext)

    const _onTapIconClick = useCallback(() => {
        if (hlsInstances.current) {
            const vd = hlsInstances.current[mainIndex][subIndexes[mainIndex]]
            const videoRef = vd.videoRef?.current
            if (!videoRef) return
            setIsMuted(false)
            videoRef.play()
            setIsLPM(false)
        }
        setVideoTapped(true)
    }, [videoTapped, isLPM, hlsInstances, mainIndex, subIndexes])

    const _pulseColor = { r: 0, g: 0, b: 0 }
    return (
        <PlayButtonContainer onClick={_onTapIconClick}>
            <CenterWrapper>
                <PulseBlob pulseColor={_pulseColor} />
                <PlayIconWrapper>
                    <Image width={64} height={64} src={PlayIcon} alt="" />
                </PlayIconWrapper>
            </CenterWrapper>
        </PlayButtonContainer>
    )
}
